import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Eye, Palette, Target, Users } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import AboutTeamShowcase from "../components/AboutTeamShowcase";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 },
  }),
};

/** Shared editorial type — both story blocks use the same scale */
const aboutHeadline =
  "font-display text-[1.65rem] font-bold leading-[1.14] tracking-[-0.02em] text-[#3e2723] sm:text-[2rem] lg:text-[2.35rem] xl:text-[2.5rem]";
const aboutBody = "text-[0.9375rem] leading-[1.75] text-[#7a5c55] sm:text-base sm:leading-[1.8]";
const aboutEyebrow =
  "text-[10px] font-bold uppercase tracking-[0.32em] text-[#3e2723]/45 sm:text-[11px]";

function FounderPortrait({ image, title, name, role, className = "" }) {
  return (
    <div
      className={`relative h-full min-h-[380px] w-full overflow-hidden rounded-[1.75rem] shadow-[0_24px_60px_-24px_rgba(62,39,35,0.35)] sm:rounded-[2rem] ${className}`}
    >
      <img src={image} alt={name || ""} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#3e2723]/90 via-[#3e2723]/15 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
        {title ? <span className={`${aboutEyebrow} text-[#fbd9e5]/65`}>{title}</span> : null}
        {name ? (
          <p className="mt-2 font-display text-xl font-bold tracking-tight text-[#fbd9e5] sm:text-2xl">{name}</p>
        ) : null}
        {role ? <p className="mt-1 text-sm text-[#fbd9e5]/78">{role}</p> : null}
      </div>
    </div>
  );
}

function DirectionPoints({ points }) {
  return (
    <ul className="mt-8 grid gap-2.5 sm:grid-cols-2 sm:gap-3">
      {points.map((point, i) => (
        <li
          key={i}
          className="group flex gap-3 rounded-2xl border border-[#3e2723]/8 bg-white/80 p-4 backdrop-blur-sm transition hover:border-[#3e2723]/15 hover:shadow-[0_8px_30px_rgba(62,39,35,0.06)]"
        >
          <span className="font-display text-lg font-black tabular-nums leading-none text-[#3e2723]/35 sm:text-xl">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-[#3e2723] sm:text-sm">{point}</span>
        </li>
      ))}
    </ul>
  );
}

export default function AboutPage() {
  const { t } = useLanguage();
  const valueIcons = [Eye, Palette, Target, Users];

  return (
    <main className="bg-white" data-testid="about-page">
      <PageHero
        variant="light"
        eyebrow={t.about.eyebrow}
        title={t.about.title}
        description={t.about.lead}
        testId="about-hero"
      />

      {/* Story — editorial block 01 */}
      <section className="relative overflow-hidden bg-white py-16 sm:py-24 lg:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-[#fbd9e5]/50 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-10 lg:px-20">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch lg:gap-10 xl:gap-14">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              className="order-2 flex lg:order-1"
            >
              <FounderPortrait
                image={t.about.team_image}
                title={t.about.team_title}
                name={t.about.team_name}
                role={t.about.team_role}
              />
            </motion.div>

            <div className="order-1 flex lg:order-2">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="flex h-full w-full flex-col justify-center py-2 sm:py-4 lg:border-l lg:border-[#3e2723]/8 lg:pl-10 xl:pl-14"
              >
                <span className={aboutEyebrow}>01 · Focus</span>
                <h2 className={`mt-4 ${aboutHeadline}`}>{t.about.p1}</h2>

                <div className="mt-6 space-y-4 border-t border-[#3e2723]/8 pt-6 sm:mt-8 sm:space-y-5 sm:pt-8">
                  <p className={aboutBody}>{t.about.p2}</p>
                  <p className={aboutBody}>{t.about.team_bio}</p>
                </div>

                <motion.div
                  variants={fadeUp}
                  custom={2}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  className="mt-8 sm:mt-10"
                >
                  <Link
                    to="/contact"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#3e2723] px-6 py-3.5 text-sm font-semibold text-[#fbd9e5] transition hover:bg-[#2a1a17] hover:-translate-y-0.5 sm:w-auto"
                    data-testid="about-cta"
                  >
                    {t.nav.cta}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Direction — editorial block 02 */}
      <section className="relative overflow-hidden bg-[#faf8f8] py-16 sm:py-24 lg:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-[#fbd9e5]/40 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-10 lg:px-20">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-12 xl:gap-14">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              className="order-1 flex flex-col justify-center"
            >
              <span className={aboutEyebrow}>02 · Direcție</span>
              <h2 className={`mt-4 ${aboutHeadline}`}>{t.about.section_image_title}</h2>
              <p className={`mt-5 sm:mt-6 ${aboutBody}`}>{t.about.section_image_body}</p>
              <DirectionPoints points={t.about.section_image_points} />
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              className="order-2 flex min-h-[320px] lg:min-h-0"
            >
              <div className="relative h-full min-h-[320px] w-full overflow-hidden rounded-[1.75rem] shadow-[0_24px_60px_-24px_rgba(62,39,35,0.28)] sm:rounded-[2rem]">
                <img
                  src={t.about.section_image}
                  alt={t.about.section_image_title || "Social Destiny"}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#3e2723]/25 via-transparent to-[#fbd9e5]/10" />
                <div className="absolute inset-x-4 bottom-4 sm:inset-x-5 sm:bottom-5">
                  <div className="flex items-center gap-3 rounded-2xl border border-white/60 bg-white/92 px-4 py-3.5 backdrop-blur-xl">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#3e2723] font-display text-xs font-bold text-[#fbd9e5] sm:h-10 sm:w-10 sm:text-sm">
                      SD
                    </span>
                    <p className="text-xs font-semibold leading-snug text-[#3e2723] sm:text-[13px]">
                      Strategie · Producție · Performanță
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AboutTeamShowcase showcase={t.about.team_showcase} members={t.about.team_members} />

      {/* Values */}
      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-20">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className={`max-w-2xl ${aboutHeadline} lg:text-[2.75rem]`}
          >
            {t.about.values_title}
          </motion.h2>
          <div className="mt-10 grid gap-4 sm:mt-14 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {t.about.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="group rounded-[1.5rem] border border-[#3e2723]/8 bg-[#faf8f8] p-6 transition duration-500 hover:-translate-y-1 hover:bg-white hover:shadow-[0_16px_48px_rgba(62,39,35,0.08)] sm:rounded-[1.75rem] sm:p-7"
                  data-testid={`value-${i}`}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#fbd9e5] text-[#3e2723] transition group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5]">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-[#3e2723] sm:mt-8 sm:text-xl">
                    {v.title}
                  </h3>
                  <p className={`mt-2.5 ${aboutBody} text-[13px] sm:text-sm`}>{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
